import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Address } from '@vsolv/core/address/domain';
import { Payment } from '@vsolv/packages/payments/domain';
import { BatchPayment } from '@wsphere/warranties/domain';
import { catchError, firstValueFrom, map, of, shareReplay } from 'rxjs';

export interface CreatedBatchPayment {
  id: string;
  warrantyCount: number;
  amount: number;
  expiryDate: string;
}

export interface CancelledBatchPayment {
  id: string;
  releasedWarranties: number;
}

/** The API's own message is the useful one — a declined card explains itself better than we can. */
export function describeBatchPaymentError(error: unknown, fallback: string) {
  const message = (error as HttpErrorResponse)?.error?.message;

  if (Array.isArray(message)) return message.join(' ');
  if (typeof message === 'string' && message.trim()) return message;

  return fallback;
}

export interface PaidBatchPayment {
  id: string;
  paymentId: string;
  amount: number;
  status: Payment.Status;
  transactionReference: string | null;
}

@Injectable()
export class BatchPaymentService {
  constructor(private http: HttpClient) {}

  /**
   * Whether this tenant has batch payments turned on. Read here rather than through warranties/web's
   * FeatureFlagsService: that lib imports this one, so importing it back would be a cycle — the same
   * reason commission.service.ts mirrors its rating shapes. Fails closed.
   *
   * refCount drops the cached value once the page using it is destroyed, so re-entering the seller
   * re-reads the flag. Without it this service outlives every page and a newly enabled tenant would
   * need a full reload — FeatureFlagsService.refresh() cannot reach across the lib boundary.
   */
  readonly isEnabled$ = this.http.get<{ feature_flags?: Record<string, boolean> }>('/api/feature-flags').pipe(
    map(response => Boolean(response?.feature_flags?.['BATCH_WARRANTY_PAYMENTS'])),
    catchError(() => of(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  create(distributorId: string, warrantyIds: string[]) {
    return firstValueFrom(
      this.http.post<CreatedBatchPayment>(`/api/distributors/${distributorId}/batch-payments`, { warrantyIds })
    );
  }

  /** Null when the seller has no live draft — an expired one reads as none. */
  retrieveOpen(distributorId: string) {
    return firstValueFrom(
      this.http.get<BatchPayment.Model | null>(`/api/distributors/${distributorId}/batch-payments/open`)
    );
  }

  cancel(batchPaymentId: string) {
    return firstValueFrom(this.http.post<CancelledBatchPayment>(`/api/batch-payments/${batchPaymentId}/cancel`, {}));
  }

  retrieveGateway(batchPaymentId: string) {
    return firstValueFrom(
      this.http.get<{ gatewayInstanceId: string }>(`/api/batch-payments/${batchPaymentId}/gateway`)
    );
  }

  /** Also the retry: a batch whose charge failed is charged again on the same payment. */
  pay(batchPaymentId: string, paymentMethodId: string, billingAddress: Address.Model) {
    return firstValueFrom(
      this.http.post<PaidBatchPayment>(`/api/batch-payments/${batchPaymentId}/pay`, {
        paymentMethodId,
        billingAddress,
      })
    );
  }
}
